<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useField, useForm } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import { getLabel } from '@/utils/data-display-utils';
import * as yup from 'yup';

import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import { constant } from '@/constant';
import { mailingListService } from '@/services/mailingList.service';
import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { showToaster } from '@/utils/toaster-util';
import { Locales } from '@/store/language.interface';

// STORE
const communityStore = useCommunityStore();
const userStore = useUserStore();

// PROPS
const props = defineProps<{
  consultationId: number;
  languageEnabled?: Locales[];
}>();

// REFS
const emailAlreadyInList = ref<boolean>(false);
const emailInputRef = ref<typeof BaseInput>();
const emailNeedsVerification = ref<boolean>(false);
const isUserSubscribed = ref<boolean>(false);
const subscriberCount = ref<number>(0);

// COMPUTED
const isUserLogged = computed(() => userStore.isUserLogged);

// FORM VALIDATION
const schema = yup.object({
  email: yup.string().when([], {
    is: () => isUserLogged.value,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.matches(constant.EmailRegex).required(),
  }),
});

// CONST
const { community } = storeToRefs(communityStore);
const { handleSubmit, setFieldValue } = useForm({
  validationSchema: schema,
  initialValues: { email: null },
});
const { value: emailField, errorMessage } = useField<string | null>('email');

// LIFECYCLE
onMounted(async () => {
  const consultation = community.value.consultations.find((x) => x.id === props.consultationId);
  isUserSubscribed.value = consultation.subscriberMailingList?.isUserSubscribed ?? false;
  subscriberCount.value = consultation.subscriberMailingList?.subscriberCount ?? 0;
  const urlSearchParams = new URLSearchParams(window.location.search);
  const code = urlSearchParams.get('code');
  if (code) {
    const successful = await mailingListService.verifyEmail(code);
    const translationKey = 'consultation.mailingList.subscription.email-verification';
    showToaster({
      message: getLabel(`${translationKey}.${successful ? 'successful' : 'unsuccessful'}`, props.languageEnabled),
      type: successful ? 'positive' : 'negative',
    });
    if (successful) subscriberCount.value++;
  }
});

// FUNCTIONS
const subscribe = handleSubmit(async (values) => {
  const subscriptionStatus = await mailingListService.subscribe(
    constant.MailingList.Parent.consultation,
    props.consultationId,
    values.email,
  );
  resetForm();
  switch (subscriptionStatus) {
    case constant.MailingList.SubscriptionStatus.AlreadySubscribed:
      emailAlreadyInList.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NeedEmailVerification:
      emailNeedsVerification.value = true;
      break;
    case constant.MailingList.SubscriptionStatus.NewlySubscribed:
      isUserSubscribed.value = true;
      subscriberCount.value++;
      break;
  }
});

async function unsubscribe() {
  const isUnsubscribed = await mailingListService.unsubscribe(
    constant.MailingList.Parent.consultation,
    props.consultationId,
  );
  if (isUnsubscribed) {
    subscriberCount.value--;
    resetForm();
  }
}

function updateEmailInput(email: string) {
  if (email) {
    emailNeedsVerification.value = false;
    emailAlreadyInList.value = false;
  }
  emailField.value = email;
}

function resetForm() {
  isUserSubscribed.value = false;
  emailNeedsVerification.value = false;
  emailAlreadyInList.value = false;
  emailField.value = null;
  setFieldValue('email', null);
  emailInputRef.value?.clearValue();
}
</script>
<template>
  <q-card>
    <q-card-section>
      <div class="context-container__mailingList">
        <div class="description">
          <span v-if="!isUserSubscribed">{{
            getLabel('consultation.mailingList.subscription.status.not-subscribed', props.languageEnabled)
          }}</span>
          <span v-else>{{
            getLabel('consultation.mailingList.subscription.status.already-subscribed', props.languageEnabled)
          }}</span>
          <span class="count">
            {{
              getLabel('consultation.mailingList.subscription.subscriber-count', props.languageEnabled, {
                named: { subscriberCount },
                plural: subscriberCount,
              })
            }}
          </span>
        </div>
        <div class="fields">
          <BaseInput
            v-if="!isUserLogged"
            :model="emailField"
            @update="updateEmailInput"
            type="email"
            :placeholder="getLabel('consultation.mailingList.subscription.email-placeholder', props.languageEnabled)"
            name="email"
            ref="emailInputRef"
          />
          <BaseButton
            v-if="!isUserSubscribed"
            :text="getLabel('consultation.mailingList.subscription.button.subscribe', props.languageEnabled)"
            @click="subscribe"
            primary
            :disabled="!isUserLogged && (!!errorMessage || emailField === null)"
          />
          <BaseButton
            v-if="isUserSubscribed && isUserLogged"
            :text="getLabel('consultation.mailingList.subscription.button.unsubscribe', props.languageEnabled)"
            @click="unsubscribe"
            primary
          />
        </div>
        <div class="status">
          <span v-if="emailAlreadyInList">
            {{ getLabel('consultation.mailingList.subscription.status.already-on-email-list', props.languageEnabled) }}
          </span>
          <span v-else-if="emailNeedsVerification">
            {{
              getLabel('consultation.mailingList.subscription.status.need-email-verification', props.languageEnabled)
            }}
          </span>
        </div>
      </div>
    </q-card-section>
  </q-card>
</template>
<style lang="scss">
.context-container__mailingList {
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: $small-text;
  font-weight: 400;
  padding: $space-sm;
  row-gap: $space-md;
  .description {
    align-self: flex-start;
    span {
      display: block;
      &.count {
        color: rgba(0, 0, 0, 0.5);
      }
    }
  }
  .fields {
    display: flex;
    flex-direction: column;
    row-gap: $space-md;
    width: 100%;
    .q-field {
      &.base-inputs {
        .q-field__control {
          border-bottom: 1px solid #000;
          border-radius: 0;
        }
        .q-field__control:after,
        .q-field__control:before {
          border: none;
        }
      }
      &.q-field--highlighted {
        .q-field__control {
          border-bottom: 2px solid #000;
        }
      }
    }
  }
}
</style>
